import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { BoxedSliderField } from "web_myplann/Inspiration/Components/BoxedSliderField";
import { EarlyFreedomAnchorButton } from "web_myplann/Inspiration/Components/EarlyFreedomAnchorButton";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import {
	EARLY_FREEDOM_LIFESTYLE_ANCHORS,
	EARLY_FREEDOM_TARGET_MAX,
	EARLY_FREEDOM_TARGET_MIN,
	EARLY_FREEDOM_TARGET_STEP,
	INSPIRATION_CURRENCY,
} from "web_myplann/Inspiration/constants";
import type { EarlyFreedomAnchorKey } from "web_myplann/Inspiration/Utils/earlyFreedomMath";

const R = Resources.Inspiration.EarlyFreedom;

type Props = {
	targetExpenses: number;
	activeAnchorKey: EarlyFreedomAnchorKey;
	onChange: (value: number) => void;
};

export const EarlyFreedomTargetSection: FC<Props> = ({
	targetExpenses,
	activeAnchorKey,
	onChange,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const activeAnchor = EARLY_FREEDOM_LIFESTYLE_ANCHORS.find(
		(anchor) => anchor.key === activeAnchorKey,
	);

	return (
		<InspirationSection title={t(R.targetTitle)} gap={1.2}>
			<Typography variant="body2" lineHeight={1.625}>
				{t(R.targetDescription)}
			</Typography>

			<Stack direction="row" gap={0.8}>
				{EARLY_FREEDOM_LIFESTYLE_ANCHORS.map((anchor) => (
					<EarlyFreedomAnchorButton
						key={anchor.key}
						icon={anchor.icon}
						label={t(anchor.copy.label)}
						amount={formatCurrency(
							anchor.key === "luxury" && anchor.key === activeAnchorKey
								? targetExpenses
								: anchor.amount,
							INSPIRATION_CURRENCY,
							0,
						)}
						selected={anchor.key === activeAnchorKey}
						onClick={() => onChange(anchor.amount)}
					/>
				))}
			</Stack>

			<BoxedSliderField
				label={t(R.targetSliderLabel)}
				value={targetExpenses}
				onChange={onChange}
				min={EARLY_FREEDOM_TARGET_MIN}
				max={EARLY_FREEDOM_TARGET_MAX}
				step={EARLY_FREEDOM_TARGET_STEP}
			/>

			{activeAnchor && (
				<Typography variant="hint" color={theme.palette.grey[500]}>
					{t(activeAnchor.copy.description)}
				</Typography>
			)}
		</InspirationSection>
	);
};
